import { useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, styled } from '@mui/material';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Select from '@/components/@IntranetLibrary/Select';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildShopManagerFeeContentHtml,
  ShopManagerFeeSnapshot,
} from '@/components/Approval/@components/ShopManagerFee';
import { getShopManagerFeeDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import {
  buildShopManagerFeeSubject,
  getShopManagerFeeYears,
  SHOP_MANAGER_FEE_BRANDS,
  SHOP_MANAGER_FEE_MONTHS,
  type ShopManagerFeeDraftSource,
} from '@/utils/approval/shopManagerFee';
import { getNestedDataFetcher } from '@/utils/apis';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const SearchRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

/** SAP 중간 관리 수수료 정산(157) — 브랜드·연도·월로 SAP 집계표를 불러와 본문 위에 표시 */
const ShopManagerFeeField = () => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { setValue, getValues } = useFormContext<ApprovalFormValues>();
  const [brand, setBrand] = useState('');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // 레거시처럼 조회 결과가 비어도 빈 표를 보여 줘야 해 form 값과 따로 둠
  const [loadedContent, setLoadedContent] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const years = useMemo(() => getShopManagerFeeYears(), []);

  const savedContent = getValues('shopManagerFee')?.content ?? null;
  const emptyContent = useMemo(
    () => buildShopManagerFeeContentHtml(null, t),
    [t],
  );

  const handleLoad = async () => {
    if (!brand) {
      snackbar.showError({ message: t('브랜드를 선택해주세요.') });
      return;
    }
    if (!year) {
      snackbar.showError({ message: t('연도를 선택해주세요.') });
      return;
    }
    if (!month) {
      snackbar.showError({ message: t('월을 선택해주세요.') });
      return;
    }
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setIsLoading(true);
    try {
      const result = await getNestedDataFetcher<ShopManagerFeeDraftSource>(
        getShopManagerFeeDraftSourceUrl(brand, year, month),
      );
      if (requestId !== requestIdRef.current) return;
      const source = result?.display?.source ?? {};
      const content = buildShopManagerFeeContentHtml(source, t);
      setLoadedContent(content);
      setValue(
        'shopManagerFee',
        source.ITEM?.length ? { content, data: source } : null,
        { shouldDirty: true },
      );
      // 레거시 — 조회할 때마다 직접 고친 제목도 덮어씀
      const brandOption = SHOP_MANAGER_FEE_BRANDS.find(
        (option) => option.value === brand,
      );
      const brandLabel = brandOption ? t(brandOption.label) : '';
      setValue(
        'subject',
        buildShopManagerFeeSubject(t, brandLabel, year, month),
        { shouldDirty: true },
      );
    } catch {
      if (requestId !== requestIdRef.current) return;
      // 데이터 없는 연월은 200(빈 목록)이라 여기 오는 건 조회 실패뿐
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  return (
    <FieldBlock>
      <SearchRow>
        <Select
          value={brand}
          placeholder={t('브랜드 선택')}
          options={SHOP_MANAGER_FEE_BRANDS.map((option) => ({
            value: option.value,
            label: t(option.label),
          }))}
          onChange={(e) => setBrand(e.target.value as string)}
          sx={{ ...approvalOutlineSelectSx, minWidth: 160 }}
        />
        <Select
          value={year}
          placeholder={t('연도 선택')}
          options={years.map((value) => ({ value, label: value }))}
          onChange={(e) => setYear(e.target.value as string)}
          sx={{ ...approvalOutlineSelectSx, minWidth: 120 }}
        />
        <Select
          value={month}
          placeholder={t('월 선택')}
          options={SHOP_MANAGER_FEE_MONTHS.map((value) => ({
            value,
            label: value,
          }))}
          onChange={(e) => setMonth(e.target.value as string)}
          sx={{ ...approvalOutlineSelectSx, minWidth: 100 }}
        />
        <BoxButton
          variant="secondary"
          disabled={isLoading}
          onClick={handleLoad}
        >
          {t('데이터 호출')}
        </BoxButton>
      </SearchRow>
      <Box sx={{ mt: 1.5 }}>
        <ShopManagerFeeSnapshot
          content={loadedContent ?? savedContent ?? emptyContent}
        />
      </Box>
    </FieldBlock>
  );
};

export default ShopManagerFeeField;
